/**
 * 2026-10-08 [센트리 NUXT3-DOYACWEB-11G / NUXT3-DOYACART-QF·QG] «화면을 떠나서 버려진 요청» 표식 — 단일 출처.
 *
 * ■ 무슨 일인가
 *   Nuxt 3.17 의 `experimental.purgeCachedData`(**기본 켜짐**)는 `useFetch` 를 부른 컴포넌트(정확히는 effect scope)가
 *   사라지면 그 요청의 결과를 버린다. 응답이 200 으로 도착해도 `status` 를 `idle` 로 되돌리고 진행 중 프라미스를
 *   취소 표시한다(nuxt 3.17.5 `dist/app/composables/asyncData.js` 의 `_off()` → nextTick `clearNuxtDataByKey()`).
 *   그러면 `await execute()` 가 **데이터 없이** 끝나고, `extractDataOrFailure` 는 그걸 «서버가 빈 응답을 줬다» 로
 *   오인해 400 «요청한 결과를 찾을 수 없습니다» 를 던졌다.
 *   로그인 사용자는 `useDYFetchWithToken` 키에 `uniqueId()` 가 붙어 호출마다 고유 키 = 그 컴포넌트가 유일한 주인이라
 *   **화면을 빨리 떠나기만 하면** 거의 항상 이렇게 된다.
 *
 * ■ 왜 문제였나
 *   사용자는 이미 다른 화면에 있으므로 이건 **오류가 아니다.** 그런데
 *   - 호출부의 `notifyError` 가 **다음 화면에 가짜 오류 알림**을 띄웠고(게시판 수정화면에서 빨리 뒤로 가면 상세에 뜸)
 *   - `showPageError` 는 떠난 주소에 에러를 기록해 **그 주소로 돌아오면 에러 화면**이 뜰 수 있었고
 *   - 센트리·서버 에러로그에 «결과 없음» 으로 쌓여 진짜 빈 응답과 섞였다.
 *
 * ■ 판별하는 곳(이 파일을 import 하는 곳)
 *   - `composables/fetch.ts` `extractDataOrFailure` — 표식을 붙여 던진다(**statusCode 400 은 그대로** — 400 을 «없음» 으로
 *     조용히 삼키는 기존 호출부의 동작이 바뀌지 않게)
 *   - `composables/utils.ts` `notifyError` / `showPageError` — 알림·에러기록·에러로그를 건너뛴다
 *   - `nuxt3-module-user-doyac/layout/DYDefault.vue` `onErrorCaptured` — 에러로그를 건너뛴다
 *   - `nuxt3-module-user-doyac/plugins/sentry.client.ts` `beforeSend` — 센트리에 보고하지 않는다
 *     (대신 `extractDataOrFailure` 의 `console.warn` 이 센트리 행동 이력(breadcrumb)에 남는다 — 그 직후 다른 진짜 에러가
 *      나면 «바로 앞에 버려진 요청이 있었다» 가 보인다)
 *
 * 🔴 의존성 없는 순수 모듈로 둔다. 센트리 플러그인도 import 하는데 `fetch.ts` 를 끌어오면 스토어까지 딸려 온다.
 */

/** `createError({ data: { [DY_DISCARDED_REQUEST]: true } })` 로 붙이는 표식 키 */
export const DY_DISCARDED_REQUEST = 'dyDiscardedRequest'

/**
 * «화면을 떠나서 버려진 요청» 에서 나온 에러인가.
 * NuxtError 를 다시 `createError()` 로 감싸는 경로가 있어 `cause` 쪽도 본다.
 * 판별 중 예외가 나도 «아니다» 로 답한다 — 에러 처리 경로에서 또 던지면 안 되므로.
 */
export function isDiscardedRequestError(error: unknown): boolean {
  try {
    const hasMark = (target: unknown): boolean => {
      if (!target || typeof target !== 'object') return false
      const data = (target as Record<string, any>).data
      return !!data && typeof data === 'object' && data[DY_DISCARDED_REQUEST] === true
    }
    if (hasMark(error)) return true
    return !!error && typeof error === 'object' && hasMark((error as Record<string, any>).cause)
  } catch {
    return false
  }
}
